import React, { MouseEvent, useContext, useState } from "react";

import {
  Assignment as AssignmentIcon,
  CameraEnhance as CameraEnhanceIcon,
  Favorite as FeedbackIcon,
  FindInPage as FindInPageIcon,
  Handshake as HandshakeIcon,
  Help as HelpIcon,
  Inventory as InventoryIcon,
  PeopleAlt as PeopleAltIcon,
  PrivacyTip as PrivacyTipIcon,
  VideoCall as VideoCallIcon,
} from "@mui/icons-material";
import ChatIcon from "@mui/icons-material/Chat";
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "@rubi/ui";
import { NavLink } from "react-router-dom";

import {
  APP_BOLO_ALERT,
  APP_CANVAS,
  APP_CONNECT,
  APP_FEEDBACK,
  APP_INTEL,
  APP_MESSAGE,
  APP_PARTNER,
  APP_REGISTRY,
  APP_REPORT_V2,
  APP_USERS_ADMIN,
} from "@/common/constants";
import { RubiModal } from "@/common/RubiModal";
import { getServiceConfig } from "@/common/serviceConfigurations";
import { isRubiconInternalEmail, mailTo } from "@/common/utils";
import { Permission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useSidebar } from "@/contexts/SidebarContext";

import { frameNavItemSx } from "./frameNavItemSx";

interface SidebarItem {
  app: string;
  label: string;
  icon: React.ReactNode;
  to?: string;
  disabled: boolean;
  ariaLabel: string;
  onClick?: () => void;
}

const AppsSideBar: React.FC = () => {
  const { selectedApp, setSelectedApp } = useSidebar();

  const [supportOpen, setSupportOpen] = useState(false);
  const { currentUser } = useContext(AuthStateContext);
  const { isDevEnv } = useDevEnvironment();

  const currentUserPermissions =
    currentUser?.permissions?.map((p) => p.title) ?? [];
  const permissions = userPermissionChecks(currentUserPermissions);
  const orgConfig = getServiceConfig(
    currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );
  const appPermissions = orgConfig?.apps ?? [];

  let reportV2To = "/rubireportv2/reports";
  if (permissions.canViewCitizenEngageRV2 && !permissions.canViewReportsRV2) {
    reportV2To = "/rubireportv2/citizen-engage";
  }

  let intelTo = "/rubiintel/ocis";
  if (permissions.canViewEntriesIntel) {
    intelTo = "/rubiintel/reports";
  }

  const supportEmailBody = `Hello Rubicon Support,
    I'm experiencing an issue on the platform.
    [Describe the issue here]
    [Attach any screenshots if you have them]
    Thank you,
    [Your Name]
    [Your Agency]
    [Your Contact Information]`;

  const topItems: SidebarItem[] = [
    {
      app: APP_REPORT_V2,
      label: "Online Reporting",
      icon: <AssignmentIcon />,
      to: reportV2To,
      disabled:
        !permissions.canAccessReportsV2App ||
        !appPermissions.includes(Permission.REPORT_V2) ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiREPORT",
    },
    {
      app: APP_INTEL,
      label: "Intelligence Management",
      icon: <FindInPageIcon />,
      to: intelTo,
      disabled:
        !permissions.canAccessIntelApp ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiINTEL",
    },
    {
      app: APP_BOLO_ALERT,
      label: "Bulletin Management",
      icon: <PrivacyTipIcon />,
      to: "/rubialert/alerts",
      disabled:
        !permissions.canAccessAlertsApp ||
        !appPermissions.includes(Permission.ALERT) ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiALERT",
    },
    {
      app: APP_CANVAS,
      label: "Camera Registry",
      icon: <CameraEnhanceIcon />,
      to: "/rubicanvas/map",
      disabled:
        !permissions.canAccessCameraRegistryApp ||
        !appPermissions.includes(Permission.CAMERA_REGISTRY) ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiCANVAS",
    },
    {
      app: APP_REGISTRY,
      label: "Registry System",
      icon: <InventoryIcon />,
      to: "/rubiregistry/registries",
      disabled:
        !permissions.canAccessRegistryApp ||
        !appPermissions.includes(Permission.REGISTRY) ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiREGISTRY",
    },
    {
      app: APP_MESSAGE,
      label: "Message System",
      icon: <ChatIcon />,
      to: "/rubimessage",
      disabled:
        !permissions.canAccessMessageApp ||
        !appPermissions.includes(Permission.MESSAGE) ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiMESSAGE",
    },
    {
      app: APP_CONNECT,
      label: "Video Calls",
      icon: <VideoCallIcon />,
      to: "/rubiconnect",
      disabled:
        !permissions.canAccessConnectApp ||
        !appPermissions.includes(Permission.CONNECT) ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiCONNECT",
    },
    // RubiFEEDBACK — dev environment only until it ships.
    ...(isDevEnv
      ? [
          {
            app: APP_FEEDBACK,
            label: "Community Feedback",
            icon: <FeedbackIcon />,
            to: "/rubifeedback",
            // TODO: re-enable permission gating once backend issues feedback.* perms
            // disabled:
            //   !permissions.canAccessFeedbackApp ||
            //   !appPermissions.includes(Permission.FEEDBACK) ||
            //   currentUser?.profile_update_required === true,
            disabled: currentUser?.profile_update_required === true,
            ariaLabel: "RubiFEEDBACK",
          },
        ]
      : []),
    {
      app: APP_PARTNER,
      label: "Partner Reporting",
      icon: <HandshakeIcon />,
      to: "/rubipartner/reports",
      disabled:
        !permissions.canAccessPartnerApp ||
        currentUser?.profile_update_required === true,
      ariaLabel: "RubiPARTNER",
    },
  ];

  const isInternalUser = isRubiconInternalEmail(currentUser?.email);

  const bottomItems: SidebarItem[] = [
    {
      app: APP_USERS_ADMIN,
      label: "User Management",
      icon: <PeopleAltIcon />,
      to: "/organizations/users",
      disabled: currentUser?.profile_update_required === true,
      ariaLabel: "RubiUSERS",
    },
    {
      app: "support",
      label: "Request Support",
      icon: <HelpIcon />,
      disabled: false,
      ariaLabel: "Request Support",
      onClick: () => setSupportOpen(true),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%", // Fill the available vertical space from MainLayout
          width: "100%",
        }}
      >
        {/* ─── Render top group of icons ─── */}
        <Box>
          {topItems.map((item) => (
            <Tooltip
              key={item.app}
              title={`${item.label}${item.disabled ? " – Disabled" : ""}`}
              placement="right"
              arrow
            >
              <span>
                <IconButton
                  component={item.to && !item.disabled ? NavLink : "button"}
                  to={item.to && !item.disabled ? item.to : undefined}
                  onClick={(
                    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
                  ) => {
                    if (item.disabled) {
                      e.preventDefault();
                      toast.clearWaitingQueue();
                      toast.error(
                        "You do not have permission to access this app. Please contact support@tryrubicon.com to request access.",
                      );
                      return;
                    }
                    if (item.onClick) item.onClick();
                    if (item.to) {
                      setSelectedApp(item.app);
                    }
                  }}
                  color="inherit"
                  size="large"
                  disabled={item.disabled}
                  sx={[
                    frameNavItemSx(selectedApp === item.app),
                    {
                      mb: 1,
                      // The active app sits on a rounded tile rather than a
                      // circle (designer's shell pass), in both modes.
                      ...(selectedApp === item.app && {
                        borderRadius: "8px",
                        ml: "4px",
                      }),
                    },
                  ]}
                  aria-label={item.ariaLabel}
                >
                  {item.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box>
          {bottomItems.map((item) => (
            <Tooltip
              key={item.app}
              title={`${item.label}${item.disabled ? "Disabled" : ""}`}
              placement="right"
              arrow
            >
              <span>
                <IconButton
                  component={item.to && !item.disabled ? NavLink : "button"}
                  to={item.to && !item.disabled ? item.to : undefined}
                  onClick={(
                    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
                  ) => {
                    if (item.disabled) {
                      e.preventDefault();
                      toast.clearWaitingQueue();
                      toast.error(
                        "You do not have permission to access this app. Please contact support@tryrubicon.com to request access.",
                      );
                      return;
                    }
                    if (item.onClick) item.onClick();
                    if (item.to) {
                      setSelectedApp(item.app);
                    }
                  }}
                  color="inherit"
                  size="large"
                  disabled={item.disabled}
                  sx={[
                    frameNavItemSx(selectedApp === item.app),
                    {
                      mb: 1,
                      // The active app sits on a rounded tile rather than a
                      // circle (designer's shell pass), in both modes.
                      ...(selectedApp === item.app && {
                        borderRadius: "8px",
                        ml: "4px",
                      }),
                    },
                  ]}
                  aria-label={item.ariaLabel}
                >
                  {item.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>
      </Box>

      <RubiModal
        isModalOpenArg={supportOpen}
        setIsModalOpenArg={setSupportOpen}
        onClose={() => setSupportOpen(false)}
      >
        <DialogTitle>Request Support</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Connect with the Rubicon team for support. Reach out if you have any
            issues or feedback.
          </Typography>

          <Typography variant="subtitle2">General Support</Typography>
          <Typography gutterBottom>
            <a
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Rubicon Support Request",
                body: supportEmailBody,
              })}
            >
              support@tryrubicon.com
            </a>
          </Typography>

          <Button
            variant="contained"
            color="primary"
            href={mailTo({
              email: "support@tryrubicon.com",
              subject: "Rubicon Support Request",
              body: supportEmailBody,
            })}
          >
            Send Email
          </Button>

          <Typography sx={{ mt: 2 }} variant="subtitle2">
            Book a Meeting
          </Typography>
          <Typography gutterBottom>
            <a href="https://calendly.com/daniyar-toronto/chat-with-rubicon">
              Calendly
            </a>
          </Typography>

          <Typography sx={{ mt: 2 }} variant="subtitle2">
            Technical Emergencies
          </Typography>
          <Typography>
            Please call: <a href="tel:1-866-260-3236">1-866-260-3236</a>
          </Typography>
        </DialogContent>
      </RubiModal>
    </>
  );
};

export default AppsSideBar;
